import type { ReactNode, Ref } from 'react';
import { cn } from '../../lib/utils.ts';

/**
 * Kartentitel (DESIGN „Seitenaufbau“ › Kartentitel, Ticket 1.271): `text-card-title`/500 (16 px, Kompakt 15) links im
 * Kartenkopf, rechts Kartenknöpfe bzw. Reiter (`actions`); optional Kurzzeile darunter (Titel → Kurzzeile `space-1`);
 * Kopf → Inhalt `space-3`. Die eine Stelle für den Stil (`.type-card-title` in styles.css); Karten mit Symbolkreis nutzen
 * `TileTitle`, Abschnitte über mehreren Karten `Section`.
 */
export function CardTitle({
  as: Tag = 'h2',
  children,
  actions,
  summary,
  inline = false,
  id,
  testId,
  focusRef,
}: {
  /** `h1` nur, wo die Karte selbst die Seite ist (Packplatz am Handy, Ticket 1.276). */
  as?: 'h1' | 'h2' | 'h3';
  /**
   * Nur die Überschrift ohne Kopfzeile und Abstand – für Köpfe, die Titel mit Marken oder Knöpfen selbst anordnen
   * (Bereichs-Stories 1.272 ff. stellen sie auf die volle Kopfzeile um).
   */
  inline?: boolean;
  children: ReactNode;
  /** Kartenknöpfe („Speichern“) oder Reiter rechts im Kopf. */
  actions?: ReactNode;
  /** Kurzzeile unter dem Titel (`text-caption` `ink-3`). */
  summary?: ReactNode;
  id?: string | undefined;
  testId?: string | undefined;
  /** Ziel für den Fokus beim Öffnen (Seitenpanel, Ticket 1.272): Titel wird per Skript fokussierbar, ohne Rahmen. */
  focusRef?: Ref<HTMLHeadingElement>;
}) {
  const heading = (
    <Tag
      ref={focusRef}
      tabIndex={focusRef ? -1 : undefined}
      id={id}
      className={cn('type-card-title m-0 min-w-0 [overflow-wrap:anywhere]', focusRef ? 'outline-none focus-visible:outline-none' : null)}
      data-card-title=""
      data-testid={testId}
    >
      {children}
    </Tag>
  );
  if (inline) return heading;
  return (
    <div className="mb-space-3 flex min-w-0 flex-wrap items-center justify-between gap-space-3" data-card-head="">
      <div className="flex min-w-0 flex-col gap-space-1">
        {heading}
        {summary ? <p className="m-0 text-caption text-ink-3">{summary}</p> : null}
      </div>
      {actions ? <div className="flex min-w-0 flex-wrap items-center gap-space-2">{actions}</div> : null}
    </div>
  );
}
